import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Button from "../Button";
import { Icon } from "@iconify/react";
import Modal from "../Modal";
import { getloanDocuments, deleteDocuments } from "../../services/loanService";
import axiosClient from "../../api/axiosClient";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { log } from "console";

/* --------------------------
   Types
   -------------------------- */
type DocKeys =
  | "aadhaar_front_file"
  | "aadhaar_back_file"
  | "pan_front_file"
  | "pan_back_file"
  | "dl_file"
  | "rc_book_file"
  | "insurance_file"
  | "salary_slip_file"
  | "bank_statement_file"
  | "agreement_file"
  | "noc_file";

interface DocumentsResponse extends Partial<Record<DocKeys, string | null>> {
  loan_id: string | number;
  uploaded_by?: number | null;
}

/* --------------------------
   Constants
   -------------------------- */
const DOC_FIELDS: Record<DocKeys, string> = {
  aadhaar_front_file: "Aadhaar Front",
  aadhaar_back_file: "Aadhaar Back",
  pan_front_file: "PAN Front",
  pan_back_file: "PAN Back",
  dl_file: "Driving Licence",
  rc_book_file: "RC Book",
  insurance_file: "Insurance",
  salary_slip_file: "Salary Slip",
  bank_statement_file: "Bank Statement",
  agreement_file: "Agreement",
  noc_file: "NOC",
};

const STORAGE_BASE =
  process.env.REACT_APP_STORAGE_URL || "https://your-server.com/storage/";

/* --------------------------
   Component
   -------------------------- */
type LoanDocumentsProps = {
  loanIdProp?: string | number;
  customerNameProp?: string;
};
 
export default function LoanDocuments({
  loanIdProp,
  customerNameProp,
}: LoanDocumentsProps) {
  const params = useParams<{ loanId?: string }>();
  const loanId = loanIdProp ?? params.loanId ?? "2";

  const [docs, setDocs] = useState<DocumentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<
    Partial<Record<DocKeys, File>>
  >({});
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const inputRefs = useRef<Partial<Record<DocKeys, HTMLInputElement | null>>>({});
      const { user } = useAuth();
        const isAdmin = user?.role === "Admin";
        const isCustomer = user?.role === "Customer";

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanId, customerNameProp]);

  /* --------------------------
     Helpers
     -------------------------- */
  function normalizeDocsPayload(payload: any): DocumentsResponse | null {
    if (!payload) return null;

    const item = Array.isArray(payload) ? payload[0] : payload;
    if (!item || typeof item !== "object") return null;

    const out: Partial<DocumentsResponse> = {
      loan_id: item.loan_id ?? item.loan?.loan_id ?? "",
      uploaded_by: item.uploaded_by ?? null,
    };

    (Object.keys(DOC_FIELDS) as DocKeys[]).forEach((k) => {
      const urlKey = `${String(k)}_url`;
      const urlVal = item[urlKey];
      const plain = item[k];
      if (urlVal) out[k] = String(urlVal);
      else if (plain) out[k] = String(plain);
      else out[k] = null;
    });

    return out as DocumentsResponse;
  }

  // helpers
  function isImagePath(p?: string | null) {
    return !!p && /\.(png|jpe?g|gif|webp|svg)$/i.test(p);
  }
  function isPdfPath(p?: string | null) {
    return !!p && /\.pdf$/i.test(p);
  }
  function isImageFile(f?: File) {
    return !!f && /^image\//i.test(f.type);
  }
  function isPdfFile(f?: File) {
    return !!f && f.type === "application/pdf";
  }

  /**
   * Manage object URLs for selectedFiles so prev URLs are revoked to avoid memory leaks.
   * We'll keep a map of previews for selectedFiles: key -> url (string)
   */
  const [filePreviews, setFilePreviews] = useState<Partial<Record<DocKeys, string>>>({});

  useEffect(() => {
    // create preview URLs for selectedFiles
    const next: Partial<Record<DocKeys, string>> = {};
    Object.keys(selectedFiles).forEach((k) => {
      const key = k as DocKeys;
      const f = selectedFiles[key];
      if (f) next[key] = URL.createObjectURL(f);
    });

    // revoke old URLs which are not in next
    Object.keys(filePreviews).forEach((k) => {
      if (!next[k as DocKeys]) {
        try { URL.revokeObjectURL(filePreviews[k as DocKeys] as string); } catch { }
      }
    });

    setFilePreviews(next);

    // cleanup on unmount => revoke all
    return () => {
      Object.values(next).forEach((u) => {
        try { if (u) URL.revokeObjectURL(u); } catch { }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles]);

  function filePreviewUrl(filePath?: string | null) {
    if (!filePath) return null;
    if (/^https?:\/\//i.test(filePath)) return filePath;
    return STORAGE_BASE.replace(/\/$/, "") + "/" + filePath.replace(/^\/+/, "");
  }

  /* --------------------------
     Fetch documents
     -------------------------- */
  async function fetchDocuments() {
    setLoading(true);
    try {
      const res = await getloanDocuments(loanId);
      const apiData = res?.data?.data ?? res?.data ?? null;
      const normalized = normalizeDocsPayload(apiData);
      setDocs(normalized);
      if (!normalized) {
        toast.error("No documents found for this loan.");
      }
    } catch (err: any) {
      console.error("fetch documents failed:", err);
      // toast.error("No documents found for this loan.");
    } finally {
      setLoading(false);
    }
  }

  /* --------------------------
     Upload flow
     -------------------------- */
  function handleFileChange(field: DocKeys, file?: File) {
    setSelectedFiles((prev) => {
      const copy = { ...prev };
      if (file) copy[field] = file;
      else delete copy[field];
      return copy;
    });
  }

  async function handleUpload(e?: React.FormEvent) {

    if (e) e.preventDefault();
    if (!loanId) return toast.error("Missing loan ID.");
    if (Object.keys(selectedFiles).length === 0)
      return toast.error("Please select at least one file to upload.");

    setUploading(true);
    try {
      const form = new FormData();
      // console.log(form);
      
      form.append("loan_id", String(loanId));
      (Object.keys(selectedFiles) as DocKeys[]).forEach((k) => {
        const f = selectedFiles[k];
        if (f) form.append(k, f);
      });

      const uploadUrl = `${process.env.REACT_APP_API_BASE || ""
        }/loans/${loanId}/documents`;

      await axiosClient.post(uploadUrl, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      await fetchDocuments();
      setSelectedFiles({});
      setShowModal(false);
      toast.success("Documents uploaded successfully!");
    } catch (err: any) {
      console.error("upload error:", err);
      toast.error(err?.response?.data?.message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  }

  /* --------------------------
     Delete flow
     -------------------------- */
  function handleDeleteClick() {
    setShowDeleteConfirm(true);
  }

  async function handleConfirmDelete() {
    if (!loanId) return toast.error("Missing loan ID.");

    setDeleteLoading(true);
    try {
      const resp = await deleteDocuments(loanId);
      setDocs(null);
      setSelectedFiles({});
      setShowModal(false);
      setShowDeleteConfirm(false);
      toast.success(resp?.message || "All documents deleted successfully.");
      await fetchDocuments();
    } catch (err: any) {
      console.error("delete error:", err);
      toast.error(err?.response?.data?.message || "Delete failed.");
    } finally {
      setDeleteLoading(false);
    }
  }

  /* --------------------------
     Render
     -------------------------- */
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Loan Documents</h2>
        <div className="gap-2 flex">
         {isAdmin &&( <Button
            variant="secondary"
            className="flex items-center"
            onClick={handleDeleteClick}
          >
            <Icon icon="mdi:delete" width={18} />
            <span className="ml-2">Delete</span>
          </Button>)}

          {!isCustomer &&(<Button
            variant="primary"
            onClick={async () => {
               setShowModal(true);
              setSelectedFiles({});
              // await fetchDocuments();
             
            }}
          >
            <Icon icon="mdi:plus" width={18} />
            Add
          </Button>)}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-500 bg-gray-50 p-6 rounded-xl shadow-sm">Loading documents...</div>
      ) : !docs ? (
        <div className="text-center text-gray-500 bg-gray-50 p-6 rounded-xl shadow-sm">No documents found.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {(Object.keys(DOC_FIELDS) as DocKeys[]).map((key) => {
            const path = docs[key];
            const url = filePreviewUrl(path);
            return (
              <div
                key={key}
                className="p-3 flex flex-col items-start gap-2 bg-white rounded-lg shadow-sm border border-gray-300"
              >
                <div className="flex items-start w-full gap-3">
                  <div className="flex-1">
                    <div className="font-medium">{DOC_FIELDS[key]}</div>
                    {/* <div className="text-sm text-gray-600 break-all">
                      {path ? String(path).split("/").pop() : "Not uploaded"}
                    </div> */}
                  </div>

                  {url && (
                    <div className="flex-shrink-0 space-x-2">
                      <Button
                        variant="secondary"
                        onClick={() => window.open(url, "_blank")}
                        className="text-sm"
                      >
                        View
                      </Button>

                    </div>
                  )}
                </div>

                {url && (
                  <div className="w-full mt-2 ">
                    {isImagePath(url) ? (
                      <img
                        src={url}
                        alt={DOC_FIELDS[key]}
                        className="w-full h-40 object-contain rounded"
                      />
                    ) : isPdfPath(url) ? (
                      // use object so pdf renders inline if browser supports it (fallback link if not)
                      <div className="w-full h-40 rounded overflow-hidden ">
                        <object
                          data={url}
                          type="application/pdf"
                          className="w-full h-full "
                        >
                          <div className="p-3 text-sm">
                            PDF preview not available.{" "}
                            <a href={url} target="_blank" rel="noopener noreferrer" className="underline">
                              Open in new tab
                            </a>
                          </div>
                        </object>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500">Preview not available</div>
                    )}
                  </div>
                )}

              </div>
            );
          })}
        </div>
      )}

      {/* Upload modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedFiles({});
        }}
        title="Upload Documents"
      >
        <form onSubmit={handleUpload}>
          <h3 className="text-lg font-semibold mb-2">Replace Or Add</h3>
          <p className="text-sm text-gray-600 mb-4">
            Choose any documents to upload. Only the selected fields will be sent.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-hidden">
            {(Object.keys(DOC_FIELDS) as DocKeys[]).map((k) => {
              const existingPath = docs ? docs[k] : null;
              const preview =
                selectedFiles[k]
                  ? URL.createObjectURL(selectedFiles[k] as File)
                  : filePreviewUrl(existingPath);

              return (
               <label
  key={k}
  className="border border-gray-300 rounded p-3 flex flex-col gap-2 cursor-pointer overflow-hidden"
>
  <div className="flex items-center justify-between">
    <div>
      <div className="font-medium">{DOC_FIELDS[k]}</div>
      {/* <div className="text-xs text-gray-500">
        {docs && docs[k] ? String(docs[k]).split("/").pop() : "No file"}
      </div> */}
    </div>

    {/* visible controls: Choose / Replace + clear */}
 <div className="flex items-center gap-2">
  {/* Choose / Replace button */}
  <button
    type="button"
    className="px-3 py-1 border rounded text-sm bg-white"
    onClick={(ev) => {
      ev.preventDefault();
      inputRefs.current[k]?.click();
    }}
  >
    {selectedFiles[k] ? "Replace" : "Choose file"}
  </button>

  {/* Show Clear button only if a new file was selected (not for already uploaded file) */}
  {selectedFiles[k] && (
    <button
      type="button"
      className="px-2 py-1 text-sm text-gray-600"
      onClick={(ev) => {
        ev.preventDefault();
        setSelectedFiles((prev) => {
          const copy = { ...prev };
          delete copy[k];
          return copy;
        });
        if (inputRefs.current[k]) {
          inputRefs.current[k]!.value = "";
        }
      }}
    >
      Clear
    </button>
  )}
</div>

  </div>

  {/* Hidden native input */}
  <input
     ref={(el) => {
    inputRefs.current[k] = el ?? null;
  }}
    type="file"
    accept="image/*,application/pdf"
    className="hidden"
    onChange={(e) => {
      const file = e.target.files?.[0] || undefined;
      handleFileChange(k, file);
    }}
  />

  {/* visible filename line */}
  <div className="text-sm text-gray-700 mt-2">
    <strong>Selected:</strong>{" "}
    {selectedFiles[k]
      ? selectedFiles[k]!.name
      : docs && docs[k]
      ? String(docs[k]).split("/").pop()
      : "No file chosen"}
  </div>

  {/* preview (use your existing preview logic — adapted to use filePreviews if you're creating object URLs) */}
  {(() => {
    const existingPath = docs ? docs[k] : null;
    const previewUrl = selectedFiles[k]
      ? URL.createObjectURL(selectedFiles[k] as File) // optional if you already manage object URLs elsewhere
      : filePreviewUrl(existingPath);

    if (!previewUrl) return null;

    if (/\.(png|jpe?g|gif|webp|svg)$/i.test(String(previewUrl))) {
      return (
        <img
          src={previewUrl}
          alt="preview"
          className="w-full h-32 object-contain rounded mt-2"
        />
      );
    } else if (/\.pdf$/i.test(String(previewUrl))) {
      return (
        <div className="w-full h-32 rounded overflow-hidden border mt-2">
          <object data={previewUrl} type="application/pdf" className="w-full h-full">
            <div className="p-2 text-sm">
              PDF preview not available.{" "}
              <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="underline">
                Open
              </a>
            </div>
          </object>
        </div>
      );
    } else {
      return <div className="text-sm text-gray-600 mt-2">File selected (no preview)</div>;
    }
  })()}
</label>
              );
            })}
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <Button
              variant="secondary"
              type="button"
              onClick={() => setSelectedFiles({})}
              disabled={uploading}
            >
              Clear
            </Button>
            <Button type="submit" variant="primary" disabled={uploading}>
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          if (!deleteLoading) setShowDeleteConfirm(false);
        }}
        title="Confirm Delete"
      >
        <div className="space-y-4">
          <p>
            Are you sure you want to delete <strong>all documents</strong> for
            this loan (ID: {loanId})? This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              disabled={deleteLoading}
            >
              {deleteLoading ? "Deleting..." : "Yes, delete all"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
